import i18n from 'i18next';
import {initReactI18next} from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import {mergeTranslation} from './utils/mergeTranslation';
import {logger} from './logger';

export const brandingLngs = new Map<string, string>();

const DEFAULT_FALLBACK_LANGUAGES = ['en'];

export {FALLBACK_LANGUAGES, ALLOWED_LANGUAGES} from './i18n.constants';

const FALLBACK_LNG_MAP: Record<string | symbol, string[]> = {
    nn: ['nb'],
    fr: ['fr-FR'],
    pt: ['pt-BR'],
    'ar-AA': ['ar'],
    'en-GB': ['en'],
    'en-US': ['en'],
    'he-IL': ['he'],
    'zh-CN': ['zh-Hans'],
    zh: ['zh-Hans'],
    'zh-HK': ['zh-Hant'],
    'zh-TW': ['zh-Hant'],
    default: DEFAULT_FALLBACK_LANGUAGES,
};

export const LANGUAGE_NAMES = {
    'zh-TW': '繁體中文',
    'zh-HK': '繁體中文',
    'zh-CN': '简体中文',
    zh: '简体中文',
    nn: 'Norsk (Nynorsk)',
    'nn-NO': 'Norsk (Nynorsk)',
    ar: 'العربية',
    'ar-AR': 'العربية',
    fr: 'Français',
    pt: 'Português',
    'pt-PT': 'Português (Portugal)',
    'pt-BR': 'Português (Brasil)',
    'en-GB': 'English (United Kingdom)',
    'en-US': 'English (United States)',
    en: 'English',
    cs: 'Česky',
    'cs-CZ': 'Česky',
    cy: 'Cymraeg',
    da: 'Dansk',
    'da-DK': 'Dansk',
    de: 'Deutsch',
    'de-DE': 'Deutsch',
    'es-ES': 'Español (España)',
    'es-US': 'Español (Estados Unidos)',
    fi: 'Suomi',
    'fi-FI': 'Suomi',
    'fr-CA': 'Français (Canada)',
    'fr-FR': 'Français',
    ga: 'Gaeilge',
    he: 'עברית',
    'he-IL': 'עברית',
    id: 'Bahasa Indonesia',
    'id-ID': 'Bahasa Indonesia',
    it: 'Italiano',
    'it-IT': 'Italiano',
    ja: '日本語',
    'ja-JP': '日本語',
    ko: '한국어',
    'ko-KR': '한국어',
    nb: 'Norsk (bokmål)',
    'nb-NO': 'Norsk (bokmål)',
    nl: 'Nederlands',
    'nl-NL': 'Nederlands',
    'nl-BE': 'Nederlands (België)',
    no: 'Norsk',
    pl: 'Polski',
    'pl-PL': 'Polski',
    sv: 'Svenska',
    'sv-SE': 'Svenska',
    th: 'ภาษาไทย',
    'th-TH': 'ภาษาไทย',
    vi: 'Tiếng Việt',
    'vi-VN': 'Tiếng Việt',
    ru: 'Русский',
    'ru-RU': 'Русский',
    hi: 'हिन्दी',
    'hi-IN': 'हिन्दी',
    tr: 'Türkçe',
    'tr-TR': 'Türkçe',
    el: 'Ελληνικά',
    'el-GR': 'Ελληνικά',
    hu: 'Magyar',
    'hu-HU': 'Magyar',
    lt: 'Lietuvių',
    'lt-LT': 'Lietuvių',
    lv: 'Latviešu',
    'lv-LV': 'Latviešu',
    ro: 'Română',
    'ro-RO': 'Română',
    sk: 'Slovenčina',
    'sk-SK': 'Slovenčina',
    sl: 'Slovenščina',
    'sl-SI': 'Slovenščina',
    bg: 'Български',
    'bg-BG': 'Български',
    uk: 'Українська',
    'uk-UA': 'Українська',
    hr: 'Hrvatski',
    'hr-HR': 'Hrvatski',
    et: 'Eesti',
    'et-EE': 'Eesti',
    'zh-Hans': '简体中文',
    'zh-Hant': '繁體中文',
};

export type LngKey = keyof typeof LANGUAGE_NAMES;

export const getLanguageName = (code: string) =>
    LANGUAGE_NAMES[code as LngKey] ?? code;

const getFallbackLanguage = (key: string) => {
    const fallbackLanguage = FALLBACK_LNG_MAP[key];
    if (fallbackLanguage) {
        return fallbackLanguage;
    }
    return DEFAULT_FALLBACK_LANGUAGES;
};

const importDefaultTranslations = async (
    language: string,
    namespace: string,
) => {
    try {
        const response = await fetch(
            new URL(`./locales/${language}/${namespace}.json`, import.meta.url),
        );
        return response.json();
    } catch (error) {
        const fallbackLngs = getFallbackLanguage(language);
        for (const lng of fallbackLngs) {
            try {
                // There is a fallback
                const fallbackTranslations = (await importDefaultTranslations(
                    lng,
                    namespace,
                )) as JSON;
                return fallbackTranslations;
            } catch {
                logger.warn('Could not import fallback language: ', lng);
            }
        }
        // Nothing found with all the fallback languages defined
        throw error;
    }
};

const importBackend = {
    type: 'backend' as const,
    read<Namespace extends string>(
        language: string,
        namespace: Namespace,
        callback: (errorValue: unknown, translations: unknown) => void,
    ) {
        const defaultTranslationsImport = importDefaultTranslations(
            language,
            namespace,
        ).catch(() => {
            logger.warn(
                {language, namespace, brandingLngs},
                'failed to load built-in language',
            );
            return {};
        });

        if (brandingLngs.has(language)) {
            return Promise.all([
                defaultTranslationsImport,
                fetch(`${brandingLngs.get(language)}`).then(r => r.json()),
            ])
                .then(([defaultTranslations, brandedTranslations]) => {
                    callback(
                        null,
                        mergeTranslation(
                            defaultTranslations,
                            brandedTranslations,
                        ),
                    );
                })
                .catch(err => callback(err, null));
        }

        defaultTranslationsImport
            .then(resources => callback(null, resources))
            .catch(err => callback(err, null));
    },
};

/**
 * @param supportedLngs - array of allowed languages (https://www.i18next.com/overview/configuration-options)
 * @param newInstance - optional boolean to avoid calling init multiple times (https://www.i18next.com/overview/api#createinstance)
 */
export function initI18next(supportedLngs: string[], newInstance?: boolean) {
    const i18nInstance = newInstance ? i18n.createInstance() : i18n;
    return i18nInstance
        .use(LanguageDetector)
        .use(importBackend)
        .use(initReactI18next)
        .init({
            supportedLngs,
            fallbackLng: FALLBACK_LNG_MAP,
            detection: {
                order: ['querystring', 'localStorage', 'navigator'],
                caches: ['localStorage'],
            },
            interpolation: {
                escapeValue: false,
            },
            react: {
                bindI18nStore: 'added',
                useSuspense: false,
            },
        });
}
